import { DateTime } from 'luxon';
import { isBusinessDay } from '../../../../api/libs/server/utils/src/parser-formatters/observed-holidays';

const GRACE_PERIOD_BUSINESS_DAYS = 5;

/**
 * Business days from the first of `day`'s month through `day`, inclusive.
 * Weekends and Reema observed holidays do not count.
 */
function businessDaysFromMonthStartThrough(day: DateTime): number {
  const timeZone = day.zoneName;
  const end = day.setZone(timeZone).startOf('day');
  let cursor = end.startOf('month');
  let count = 0;
  while (cursor <= end) {
    if (isBusinessDay(cursor, timeZone)) {
      count += 1;
    }
    cursor = cursor.plus({ days: 1 });
  }
  return count;
}

/**
 * Business days still left in the grace window, including today when today
 * is a business day.
 */
function remainingGraceBusinessDays(currentDate: DateTime): number {
  const timeZone = currentDate.zoneName;
  const day = currentDate.setZone(timeZone).startOf('day');
  const businessDaysElapsed = businessDaysFromMonthStartThrough(day);
  const businessDaysBeforeToday = isBusinessDay(day, timeZone)
    ? businessDaysElapsed - 1
    : businessDaysElapsed;
  return GRACE_PERIOD_BUSINESS_DAYS - businessDaysBeforeToday;
}

/**
 * Helper function to convert various date formats to DateTime
 */
function normalizeDate(date: DateTime | number | string): DateTime {
  if (typeof date === 'number') {
    return DateTime.fromMillis(date);
  }
  if (typeof date === 'string') {
    return DateTime.fromFormat(date, 'yyyy-MM-dd');
  }
  return date;
}

/**
 * Determines the minimum allowed date for status change requests based on business rules.
 *
 * Business Rule: Status change requests can't be requested for the previous month,
 * except during the first 5 business days of the following month. Weekends and
 * Reema observed holidays are not business days, and the window stays open
 * through the end of the 5th business day.
 *
 * Note: This function uses the current date in the user's local timezone.
 *
 * @param currentDate - The current date (defaults to DateTime.now())
 * @returns The minimum allowed date for status change requests
 */
export function getMinDateForStatusChangeRequest(
  currentDate: DateTime = DateTime.now()
): DateTime {
  const monthStart = currentDate.startOf('month');

  if (isInGracePeriodForStatusChangeRequest(currentDate)) {
    // If we're in the grace period, allow requests for the previous month
    return monthStart.minus({ month: 1 });
  } else {
    // Otherwise, only allow requests for the current month onwards
    return monthStart;
  }
}

/**
 * Determines if a status change request can be approved based on when it is supposed to take effect.
 * A request for a date that was only allowed during the grace period cannot be approved after the grace period has elapsed.
 *
 * @param requestEffectiveAt - When the status change is supposed to take effect (DateTime, millis, or 'yyyy-MM-dd' string)
 * @param currentDate - The current date (defaults to DateTime.now())
 * @returns True if the request can be approved, false otherwise
 */
export function canApproveStatusChangeRequest(
  requestEffectiveAt: DateTime | number | string,
  currentDate: DateTime = DateTime.now()
): boolean {
  const effectiveDate = normalizeDate(requestEffectiveAt);
  const minDate = getMinDateForStatusChangeRequest(currentDate);
  return effectiveDate >= minDate;
}

/**
 * Gets a human-readable description of the current date restrictions.
 *
 * @param currentDate - The current date (defaults to DateTime.now())
 * @returns A string describing the current restrictions
 */
export function getStatusChangeRequestDateRestrictionDescription(
  currentDate: DateTime = DateTime.now()
): string {
  if (isInGracePeriodForStatusChangeRequest(currentDate)) {
    const remainingDays = remainingGraceBusinessDays(currentDate);
    return `You can request status changes for the previous month through the first ${GRACE_PERIOD_BUSINESS_DAYS} business days of the current month. You have ${remainingDays} business day${
      remainingDays !== 1 ? 's' : ''
    } remaining.`;
  } else {
    return 'You can only request status changes for the current month onwards.';
  }
}

/**
 * Checks if we're currently in the grace period for status change requests.
 * The grace period runs from the first of the month through the 5th business
 * day. Weekends and Reema observed holidays inside that span stay inside the
 * window; it closes after the 5th business day.
 *
 * @param currentDate - The current date (defaults to DateTime.now())
 * @returns True if we're in the grace period, false otherwise
 */
export function isInGracePeriodForStatusChangeRequest(
  currentDate: DateTime = DateTime.now()
): boolean {
  const timeZone = currentDate.zoneName;
  const day = currentDate.setZone(timeZone).startOf('day');
  const businessDaysElapsed = businessDaysFromMonthStartThrough(day);
  if (isBusinessDay(day, timeZone)) {
    return businessDaysElapsed <= GRACE_PERIOD_BUSINESS_DAYS;
  }
  return businessDaysElapsed < GRACE_PERIOD_BUSINESS_DAYS;
}
